/**
 * Passer d'un document à un autre DANS la même conversation (2026-09-22).
 *
 * Le geste que rend possible « plusieurs brouillons par conversation » : l'avocat
 * a une assignation et un courrier dans le même fil, et revient sur l'un sans
 * perdre l'autre.
 *
 * ## Ce qui le distingue de « Copier un brouillon d’une autre conversation »
 *
 * Le menu porte deux entrées qui se ressemblent et ne font pas la même chose :
 *
 * - **transverse** (`CanvasOpenMenu.handleSelectDraft`) : le brouillon vient d'un
 *   AUTRE fil, on en **copie** le contenu. L'original reste chez lui — il ne peut
 *   pas changer de conversation, le serveur le refuse (`requireMonotoneDraftScope`) ;
 * - **interne** (ici) : on **adopte** l'identité du brouillon. Sans cela, basculer
 *   de A vers B réenregistrerait B sous un troisième identifiant et l'avocat
 *   accumulerait des copies jusqu'au plafond sans comprendre pourquoi.
 */

import { CANVAS_DRAFTS_PER_CONVERSATION_LIMIT, type CanvasDocumentDepart, type CanvasDraft } from '@hector/shared-types';
import { canvasDraftApi } from '../api/canvasDraft.api';
import { canvasDocumentRuntime } from './canvasDocumentRuntime';
import { prepareDocx } from '@hector/docx-integrity';
import { greffeLeModeleDePage } from '../editor/greffeDuModeleDePage';
import {
  basculerVersUnBrouillonDuFil,
  captureCanvasResidentRecovery,
  captureCanvasResidentReplacementTarget,
  startBlankCanvasResident,
  startCanvasResidentOnPageModel,
} from './canvasResidentPresentation';
import { isCanvasSessionProtectedByWork } from './canvasSessionProtection';

/** Ce que le geste a produit — l'appelant en tire son message, jamais une exception. */
export type IssueDeBascule =
  | { readonly issue: 'bascule' }
  /** Un travail protège ce document : rien n'a été touché. */
  | { readonly issue: 'protege' }
  /** Le montage a changé pendant le réseau : le document visible n'est pas celui qu'on visait. */
  | { readonly issue: 'montage-remplace' }
  /** Le document SORTANT n'a pas pu être enregistré : on ne bascule pas. */
  | { readonly issue: 'sortant-non-enregistre'; readonly detail: string }
  /** Le contenu n'a pas pu être lu, ou le document n'a pas pu être posé. */
  | { readonly issue: 'echec'; readonly detail: string };

interface DependancesDeBascule {
  readonly lireLeContenu: (draftId: string) => Promise<Blob>;
  /** Enregistre le document SORTANT et REND LA MAIN seulement quand c'est fait. */
  readonly enregistrerLeBrouillonCourant: (conversationId: string) => Promise<ResultatDEnregistrement>;
}

/** Ce que l'enregistrement du document sortant a donné. */
export type ResultatDEnregistrement =
  | { readonly ok: true }
  | { readonly ok: false; readonly detail: string };

/**
 * Enregistre le document sortant AVANT qu'on lâche son identité.
 *
 * ⚠️ **Ce geste devait être attendu, et ne l'était pas** (défaut corrigé le
 * 2026-09-22, après l'avoir vu sur le vrai contrôleur). Les trois gestes qui
 * changent de document lançaient `flush()` — qui part sans être attendu — puis
 * appelaient aussitôt `clearDraft()` ou `restoreDraft()`. Ces deux méthodes
 * passent par `invalidatePending`, qui **avorte les PUT en vol** et périme la
 * génération du contrôleur : l'export partait bien, son envoi mourait une
 * instruction plus tard. Mesuré : `flush()` seul émet 1 PUT, `flush()` suivi de
 * `clearDraft()` en émet **0**.
 *
 * Pour l'avocat : ce qu'il avait tapé depuis le dernier enregistrement abouti
 * disparaissait. Sur un document neuf tapé d'un trait, aucun enregistrement
 * n'avait jamais abouti — le document ENTIER disparaissait, sans même
 * apparaître dans « Documents de cette conversation ».
 *
 * ⚠️ **Le remède n'est pas de retirer l'invalidation** : c'est elle qui empêche
 * le document entrant d'être enregistré sous l'identité du sortant. Il faut
 * attendre le reçu, puis invalider.
 *
 * ⚠️ **`checkpoint()` REJETTE quand il n'y a rien à enregistrer** (« no
 * persistable captured version is available »), ce qui est le cas d'un canvas
 * neuf jamais touché. L'appeler sans condition bloquerait « Nouveau » sur une
 * page blanche. On ne l'appelle donc que si le contrôleur porte une version
 * plus récente que celle qui est enregistrée.
 *
 * ⚠️ **On RATTRAPE les frappes faites pendant l'envoi** (2026-09-23). Un
 * checkpoint enregistre la version capturée à son départ ; ce que l'avocat tape
 * pendant le PUT forme une version plus récente, que l'invalidation qui suit
 * perdrait tout autant. La boucle recommence tant que le contrôleur a une
 * avance, bornée pour ne pas retenir le geste d'un avocat qui tape sans arrêt.
 * Contrat pour l'appelant : ne RIEN attendre entre ce retour et le
 * remplacement, sinon la fenêtre se rouvre.
 */
export async function enregistrerLeBrouillonCourant(
  conversationId: string,
): Promise<ResultatDEnregistrement> {
  const controller = canvasDocumentRuntime.getDraftController(conversationId);
  // Pas de contrôleur : aucun document n'est adressable, il n'y a rien à perdre.
  if (!controller) return { ok: true };
  for (let tour = 0; tour < RATTRAPAGES_MAX; tour += 1) {
    const { version, savedVersion } = controller.getSnapshot();
    if (savedVersion >= version) return { ok: true };
    try {
      await controller.checkpoint();
    } catch (err: unknown) {
      return { ok: false, detail: err instanceof Error ? err.message : String(err) };
    }
  }
  const { version, savedVersion } = controller.getSnapshot();
  if (savedVersion >= version) return { ok: true };
  return {
    ok: false,
    detail: `le document change encore pendant son enregistrement (version ${version}, `
      + `enregistrée ${savedVersion}, conversationId=${conversationId}) — laisse-le au repos un instant, puis réessaie`,
  };
}

/**
 * Combien d'envois on enchaîne pour rattraper un avocat qui tape pendant
 * l'enregistrement. Trois couvre la frappe ordinaire (chaque envoi dure moins
 * d'une seconde) sans retenir indéfiniment le geste de celui qui ne s'arrête pas.
 */
const RATTRAPAGES_MAX = 3;

/** Ce que la préparation de la sortie du document courant a donné. */
export type SortieDuDocumentCourant =
  | { readonly ok: true }
  | { readonly ok: false; readonly raison: 'sortant-non-enregistre'; readonly detail: string }
  | { readonly ok: false; readonly raison: 'plafond'; readonly limite: number };

export interface DependancesDeSortie {
  readonly enregistrer: (conversationId: string) => Promise<ResultatDEnregistrement>;
  readonly compterLesDocumentsDuFil: (conversationId: string) => Promise<number>;
  /**
   * Le document sortant n'a-t-il encore AUCUNE ligne en base, alors qu'il a du
   * contenu à enregistrer ? Son enregistrement va alors créer un document de
   * plus dans le fil, que le comptage fait avant ne voit pas encore.
   */
  readonly sortantVaCreerUneLigne: (conversationId: string) => boolean;
}

function sortantVaCreerUneLigne(conversationId: string): boolean {
  const controller = canvasDocumentRuntime.getDraftController(conversationId);
  if (!controller) return false;
  const { draftId, version, savedVersion } = controller.getSnapshot();
  return draftId === null && version > savedVersion;
}

/**
 * Tout ce qui doit être vrai AVANT de poser un autre document à la place du
 * document ouvert — le même préalable pour les sept portes (« Nouveau »,
 * bascule, copie d'un brouillon d'une autre conversation, ouverture d'une pièce
 * depuis le dossier ou depuis « Mes écritures », et les deux outils agent).
 *
 * 1. Si le geste CRÉE un document dans le fil, le plafond est vérifié. Sans
 *    cette lecture préalable, l'avocat rédigerait dans un document que le
 *    serveur refusera au premier enregistrement. Le document sortant compte
 *    s'il n'a pas encore de ligne mais va en créer une en s'enregistrant.
 *    Fail-open : une lecture en échec laisse passer, le serveur reste
 *    l'autorité.
 * 2. PUIS le document sortant est enregistré, et son reçu attendu. C'est la
 *    DERNIÈRE attente : une frappe faite pendant la lecture du plafond est donc
 *    enregistrée elle aussi. (Compter après enregistrer obligeait à enregistrer
 *    une seconde fois pour rattraper ces frappes — revue du 2026-09-23.)
 *
 * ⚠️ L'appelant ne doit plus RIEN attendre entre ce retour et le remplacement :
 * toute attente rouvrirait la fenêtre où une frappe est perdue.
 */
export async function preparerLaSortieDuDocumentCourant(
  conversationId: string,
  options: { readonly creeUnDocument: boolean },
  deps: DependancesDeSortie = {
    enregistrer: enregistrerLeBrouillonCourant,
    compterLesDocumentsDuFil: async (id) => (await canvasDraftApi.listByConversation(id)).length,
    sortantVaCreerUneLigne,
  },
): Promise<SortieDuDocumentCourant> {
  if (options.creeUnDocument) {
    let tenus = 0;
    try {
      tenus = await deps.compterLesDocumentsDuFil(conversationId);
    } catch {
      // Laisser passer : le refus serveur porte la même limite, en dernier recours.
    }
    if (deps.sortantVaCreerUneLigne(conversationId)) tenus += 1;
    if (tenus >= CANVAS_DRAFTS_PER_CONVERSATION_LIMIT) {
      return { ok: false, raison: 'plafond', limite: CANVAS_DRAFTS_PER_CONVERSATION_LIMIT };
    }
  }
  const enregistrement = await deps.enregistrer(conversationId);
  if (!enregistrement.ok) return { ok: false, raison: 'sortant-non-enregistre', detail: enregistrement.detail };
  return { ok: true };
}

/** Le refus d'une sortie, dit à l'avocat — une seule formulation pour toutes les portes. */
export function decrireLeRefusDeSortie(
  refus: Exclude<SortieDuDocumentCourant, { ok: true }>,
): string {
  if (refus.raison === 'plafond') {
    return `Cette conversation contient déjà ${refus.limite} documents. `
      + 'Supprimez-en un depuis le menu « Ouvrir » avant d’en ouvrir un nouveau.';
  }
  return `Le document ouvert n'a pas pu être enregistré : ${refus.detail}. `
    + 'Rien n\'a été changé — réessaie, ou utilise « Réessayer » dans le bandeau.';
}

/**
 * Le refus d'une sortie de document, dit à l'AGENT.
 *
 * La contrepartie de `decrireLeRefusDeSortie`, écrite pour l'avocat : elle nomme
 * la conversation, dit que rien n'a été remplacé, et donne la suite à tenir.
 *
 * Un plafond atteint n'est pas une panne à réessayer, et depuis que tout
 * document neuf passe par une page neuve (2026-10-09) c'est un refus que
 * l'agent rencontre : chaque page neuve crée un document. Le message porte donc
 * les DEUX issues — ce que l'avocat doit faire (supprimer un document, à
 * l'endroit exact où il les voit) et ce que l'agent peut encore faire sans lui
 * (travailler dans le document ouvert). Sans la seconde, l'agent s'arrêtait
 * alors que la demande pouvait souvent se servir en place. Elle ne vaut que
 * pour une demande qui CHANGE ce document, et le message le borne : sans
 * cela l'agent transformerait le document ouvert en l'autre document demandé
 * (un mail à la place de l'acte).
 */
export function decrireLeRefusDeSortiePourLAgent(
  conversationId: string,
  refus: Exclude<SortieDuDocumentCourant, { ok: true }>,
): string {
  if (refus.raison === 'plafond') {
    return `conversationId=${conversationId} already holds ${refus.limite} canvas documents, the maximum, so no new document `
      + 'can be created. Nothing was replaced and the lawyer still sees the current document. Do not retry. '
      + 'Tell the lawyer to delete a document in the canvas menu « Ouvrir », section « Documents de cette conversation », '
      + 'before a new one can be opened. Until then, a request that CHANGES the document that is open can still be served: '
      + 'read it with canvas_inspect and change it in place. A request for ANOTHER document (a mail, a note, a new act) '
      + 'cannot: never write it over the open document, whose text and formatting would be lost.';
  }
  return `the document currently open in conversationId=${conversationId} could not be saved before being `
    + `replaced (${refus.detail}). Nothing was replaced, so no keystroke was lost. Retry once; if it fails again, `
    + 'tell the lawyer the document cannot be saved right now.';
}

/**
 * @param conversationId le PORTEUR du canvas, jamais la conversation affichée.
 * @param draft le brouillon à monter — il doit appartenir à ce fil.
 */
export async function basculerVersLeBrouillon(
  conversationId: string,
  draft: CanvasDraft,
  deps: DependancesDeBascule,
): Promise<IssueDeBascule> {
  if (draft.conversationId !== conversationId) {
    return {
      issue: 'echec',
      detail: `Ce document appartient à une autre conversation (draftId=${draft.id}). `
        + 'Ouvre-le depuis « Copier un brouillon d’une autre conversation », qui en copie le contenu.',
    };
  }
  // La protection exacte se relit AU MOMENT DU GESTE, hors React : la prop du
  // composant est figée au dernier rendu, et un travail peut démarrer dans un
  // WebSocket sans geste supplémentaire de l'avocat (#893/#1044).
  if (isCanvasSessionProtectedByWork(conversationId)) return { issue: 'protege' };

  // Capturée AVANT toute attente : c'est l'intention du clic. Un remontage
  // survenu pendant le réseau se voit alors au remplacement (`montage-remplace`).
  const target = captureCanvasResidentReplacementTarget({ conversationId });
  // Le contenu ENTRANT d'abord. Dans l'ordre inverse, tout ce que l'avocat
  // tapait pendant ce téléchargement tombait APRÈS l'enregistrement du sortant
  // et disparaissait à l'adoption (constat de revue du 2026-09-23).
  let blob: Blob;
  try {
    blob = await deps.lireLeContenu(draft.id);
  } catch (err: unknown) {
    return { issue: 'echec', detail: err instanceof Error ? err.message : String(err) };
  }
  // RE-CONTRÔLE APRÈS L'ATTENTE : le téléchargement dure, et une rédaction a pu
  // démarrer pendant. Sans ce second appel, la garde d'entrée ne protège que
  // l'instant du clic, pas celui de l'écriture.
  if (isCanvasSessionProtectedByWork(conversationId)) return { issue: 'protege' };

  // Le document SORTANT est enregistré, et on ATTEND son reçu : l'adoption qui
  // suit périme la génération du contrôleur et avorterait un envoi encore en
  // vol. Un échec arrête le geste — basculer quand même perdrait les dernières
  // frappes en annonçant un succès. C'est la DERNIÈRE attente du geste : tout
  // ce qui suit est synchrone, aucune frappe ne peut plus s'intercaler.
  const enregistrement = await deps.enregistrerLeBrouillonCourant(conversationId);
  if (!enregistrement.ok) {
    return { issue: 'sortant-non-enregistre', detail: enregistrement.detail };
  }
  if (isCanvasSessionProtectedByWork(conversationId)) return { issue: 'protege' };

  const decision = basculerVersUnBrouillonDuFil({ target, blob, draft });
  if (!decision.accepted) return { issue: 'montage-remplace' };

  // L'ADOPTION, et c'est elle qui fait tout : le contrôleur survit au
  // remplacement (il vit avec la conversation, pas avec le montage), donc il
  // continuerait d'enregistrer sous l'identifiant du document précédent.
  const identite = poserLIdentiteDuDocument(conversationId, {
    kind: 'brouillon-du-fil', draftId: draft.id, integrityAttemptId: draft.integrityAttemptId,
  });
  if (!identite.adopte) {
    return {
      issue: 'echec',
      detail: `« ${draft.title} » est ouvert, mais comme un nouveau document : son identité n'a pas pu `
        + `être reprise (draftId=${draft.id}). Les modifications iront dans une copie ; l'original et le `
        + 'document précédent restent intacts dans « Documents de cette conversation ».',
    };
  }
  return { issue: 'bascule' };
}

/** Ce que « Nouveau » a produit — l'appelant en tire son message. */
export type IssueDuDocumentVierge =
  /** `mountEpoch` : l'époque que la page neuve a allouée — la seule dont on peut attendre le montage. */
  | { readonly issue: 'vierge'; readonly mountEpoch: number }
  | { readonly issue: 'protege' }
  | { readonly issue: 'montage-remplace' }
  | { readonly issue: 'refus-de-sortie'; readonly refus: Exclude<SortieDuDocumentCourant, { ok: true }> };

/**
 * « Nouveau » : poser une page blanche à la place du document ouvert, qui reste
 * dans « Documents de cette conversation ».
 *
 * ⚠️ **Le bail exclusif se prend APRÈS l'enregistrement du sortant, jamais
 * avant** (défaut corrigé le 2026-09-23, introduit la veille). Le bail ouvre une
 * activité `mutation` sur la session ; le contrôleur lit cette activité comme
 * une écriture en cours et REFUSE tout checkpoint pendant qu'elle vit. Pris
 * avant, il faisait échouer l'enregistrement dès que l'avocat avait tapé
 * quelque chose — et le retour anticipé ne le rendait pas : l'activité restait
 * ouverte pour la vie de l'onglet, l'enregistrement automatique se reportait
 * indéfiniment, et plus rien de ce document n'était enregistré.
 *
 * Extrait de `RedactionPanel` pour être jugé sur le vrai runtime : c'est la
 * rencontre du bail et du contrôleur qui portait le défaut, et aucun test à
 * doubles ne peut la reproduire.
 *
 * @param depart — le papier à en-tête dont part la page neuve, choisi par l'avocat sur le voile
 *   de choix (`CanvasDepartChoixVoile`) ou par l'agent (`modele_de_page` d'un travail de
 *   rédaction) ; absent = le défaut du cabinet.
 * @param epingleDuTravail — l'épingle AUTHENTIQUE du travail de l'agent qui demande la page neuve
 *   (épingle du tour ou de l'admission). Elle protège le document qu'elle remplace : la garde
 *   « document en activité » ne la compte donc pas, et le bail la PRÉSENTE au lieu d'exiger
 *   qu'aucune épingle ne tienne le document (#1354/D10). Absente : le geste de l'avocat.
 */
export async function ouvrirUnDocumentVierge(
  conversationId: string,
  preparer: (conversationId: string) => Promise<SortieDuDocumentCourant>
    = (id) => preparerLaSortieDuDocumentCourant(id, { creeUnDocument: true }),
  depart?: CanvasDocumentDepart,
  epingleDuTravail?: () => void,
): Promise<IssueDuDocumentVierge> {
  // Relue AU MOMENT DU GESTE, hors React (#893) : la prop du composant est figée
  // au dernier rendu, et un tour d'agent a pu démarrer depuis. Un travail de l'agent
  // se protège lui-même : c'est le bail, qui écarte sa propre épingle, qui juge alors.
  const protege = (): boolean => epingleDuTravail === undefined && isCanvasSessionProtectedByWork(conversationId);
  if (protege()) return { issue: 'protege' };
  const target = captureCanvasResidentReplacementTarget({ conversationId });
  const sortie = await preparer(conversationId);
  if (!sortie.ok) return { issue: 'refus-de-sortie', refus: sortie };
  // Plus AUCUNE attente à partir d'ici : le bail, le remplacement et l'oubli
  // d'identité s'enchaînent dans la même tâche.
  if (protege()) return { issue: 'protege' };
  const bail = canvasDocumentRuntime.tryBeginExclusiveReplacement(conversationId, epingleDuTravail);
  if (!bail) return { issue: 'protege' };
  try {
    const remplacement = startBlankCanvasResident({ target, replacementLease: bail, depart });
    if (!remplacement.accepted) return { issue: 'montage-remplace' };
    // Le contrôleur survit au remplacement : sans cet oubli explicite, la page
    // blanche s'enregistrerait PAR-DESSUS le document qu'on vient de quitter.
    detacherLeDocumentCourant(conversationId);
    return { issue: 'vierge', mountEpoch: remplacement.session.mountEpoch };
  } finally {
    // Rendu sur TOUS les chemins : un bail oublié garde la session « en
    // écriture », et l'enregistrement automatique ne repart plus jamais.
    bail.release();
  }
}

/** Ce que « Changer de papier à en-tête » a produit — l'appelant en tire son message. */
export type IssueDuChangementDeModele =
  | { readonly issue: 'change' }
  | { readonly issue: 'protege' }
  | { readonly issue: 'montage-remplace' }
  /** L'avocat a écrit pendant la préparation : rien n'est remplacé, sa frappe est intacte. */
  | { readonly issue: 'document-modifie' }
  /** Le document ou le modèle ne se greffe pas : rien n'est remplacé. `cause` est technique. */
  | { readonly issue: 'echec'; readonly cause: string }
  | { readonly issue: 'refus-de-sortie'; readonly refus: Exclude<SortieDuDocumentCourant, { ok: true }> };

/**
 * « Changer de papier à en-tête » : le document ouvert, texte compris, remis sur un autre modèle
 * de page du cabinet (en-tête, pied de page, marges). Le résultat est un document NEUF du fil ;
 * celui d'avant reste dans « Documents de cette conversation ».
 *
 * L'ordre suit `ouvrirUnDocumentVierge`, avec deux étapes de plus AVANT la sortie :
 * 1. exporter le document tel qu'il est à l'écran (`captureCanvasResidentRecovery`, qui refuse
 *    s'il change pendant l'export) ;
 * 2. télécharger le modèle, greffer, et passer le résultat à la porte d'intégrité.
 * Puis l'enregistrement du sortant, DERNIÈRE attente, et le remplacement sans plus rien attendre.
 *
 * ⚠️ Une frappe entre l'export et l'enregistrement du sortant est enregistrée dans le document
 * d'AVANT, mais absente du document greffé : le geste se refuse alors (`document-modifie`) au
 * lieu de poser une version qui la perd. Le juge est la version du contrôleur de brouillon,
 * relue après l'enregistrement.
 *
 * @param chargerLeModele — les octets du papier à en-tête choisi (`fetchCabinetPaperBlobById`).
 * @param titre — le titre affiché du document, repris par le document neuf.
 */
export async function changerLeModeleDePage(
  conversationId: string,
  options: { readonly chargerLeModele: () => Promise<Blob>; readonly titre: string | null },
  preparer: (conversationId: string) => Promise<SortieDuDocumentCourant>
    = (id) => preparerLaSortieDuDocumentCourant(id, { creeUnDocument: true }),
): Promise<IssueDuChangementDeModele> {
  if (isCanvasSessionProtectedByWork(conversationId)) return { issue: 'protege' };
  const target = captureCanvasResidentReplacementTarget({ conversationId });
  const controleur = canvasDocumentRuntime.getDraftController(conversationId);
  if (!controleur) return { issue: 'echec', cause: `aucun contrôleur de brouillon pour conversationId=${conversationId}` };

  let greffe: Blob;
  let versionExportee: number;
  try {
    versionExportee = controleur.getSnapshot().version;
    const capture = await captureCanvasResidentRecovery(target);
    const document = capture.presentation.templateOverride;
    if (!document) return { issue: 'echec', cause: "l'export du document ouvert n'a rendu aucun fichier" };
    const [octetsDuDocument, octetsDuModele] = await Promise.all([
      document.arrayBuffer(),
      options.chargerLeModele().then((modele) => modele.arrayBuffer()),
    ]);
    const octets = await greffeLeModeleDePage(octetsDuDocument, octetsDuModele);
    const verdict = await prepareDocx(new Uint8Array(octets));
    if (verdict.status === 'rejected') {
      return {
        issue: 'echec',
        cause: `porte d'intégrité : ${verdict.issues.map((i) => [i.code, i.part, i.location].filter(Boolean).join(' ')).join(', ')}`,
      };
    }
    greffe = new Blob([verdict.status === 'repaired' ? new Uint8Array(verdict.bytes) : octets], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
  } catch (error) {
    return { issue: 'echec', cause: error instanceof Error ? error.message : String(error) };
  }

  if (isCanvasSessionProtectedByWork(conversationId)) return { issue: 'protege' };
  const sortie = await preparer(conversationId);
  if (!sortie.ok) return { issue: 'refus-de-sortie', refus: sortie };
  // Plus AUCUNE attente à partir d'ici, comme pour « Nouveau ».
  if (controleur.getSnapshot().version !== versionExportee) return { issue: 'document-modifie' };
  if (isCanvasSessionProtectedByWork(conversationId)) return { issue: 'protege' };
  const bail = canvasDocumentRuntime.tryBeginExclusiveReplacement(conversationId);
  if (!bail) return { issue: 'protege' };
  try {
    const remplacement = startCanvasResidentOnPageModel({
      target, replacementLease: bail, blob: greffe, titre: options.titre,
    });
    if (!remplacement.accepted) return { issue: 'montage-remplace' };
    detacherLeDocumentCourant(conversationId);
    return { issue: 'change' };
  } finally {
    bail.release();
  }
}

/**
 * Détacher le document courant pour que ce qui sera posé à sa place devienne un
 * document NEUF du fil.
 *
 * ⚠️ **À appeler après CHAQUE copie, et c'est un défaut corrigé le 2026-09-22.**
 * Un remplacement ne touche pas au contrôleur d'enregistrement : il continue de
 * pointer sur le document sortant. Le contenu copié partait donc au prochain
 * enregistrement **sous l'identifiant du document courant**, qui était écrasé —
 * l'original copié, lui, restait intact chez lui. C'était cohérent tant qu'un
 * fil ne portait qu'un document (« remplacer le document de ce fil ») ; ça ne
 * l'est plus.
 *
 * ⚠️ L'enregistrement du sortant doit avoir ABOUTI avant, pas seulement démarré
 * (corrigé le 2026-09-22) : `clearDraft` avorte les PUT en vol. Passer par
 * `preparerLaSortieDuDocumentCourant` et attendre son reçu.
 *
 * ⚠️ **Juste après le remplacement, AVANT que le nouveau montage soit prêt.**
 * Au signal `ready`, le contrôleur arme le checkpoint initial de la source
 * (`confirmerLeCheckpointInitial`) avec l'identité qu'il porte À CET INSTANT.
 * Détacher après ce signal arrive trop tard des deux côtés : l'armement a
 * capturé l'identité du sortant, et `clearDraft` oublie la source — la pièce
 * posée ne s'enregistre plus du tout avant la première frappe.
 */
export function detacherLeDocumentCourant(conversationId: string): void {
  canvasDocumentRuntime.getDraftController(conversationId)?.clearDraft();
}

/** Sous quelle identité le document qu'on vient de poser s'enregistrera. */
export type IdentiteDuDocumentPose =
  /** Pièce, gabarit, page neuve, copie d'un autre fil : un document NEUF du fil. */
  | { readonly kind: 'nouveau-document' }
  /** Un brouillon de CE fil, dont on reprend l'identité. */
  | { readonly kind: 'brouillon-du-fil'; readonly draftId: string; readonly integrityAttemptId?: string };

/**
 * Donne au contrôleur d'enregistrement l'identité du document qu'on vient de
 * poser — le geste commun au menu et à l'agent.
 *
 * ⚠️ **Dans la même tâche que le remplacement, AVANT le signal `ready`** : au
 * `ready`, le contrôleur arme le premier enregistrement avec l'identité qu'il
 * porte à cet instant (voir {@link detacherLeDocumentCourant}). L'agent
 * adoptait après `ready` et comptait sur le délai d'enregistrement pour annuler
 * l'envoi parti sous l'identité du sortant (revue du 2026-09-23).
 *
 * ⚠️ **Une adoption refusée DÉTACHE, elle ne laisse jamais le contrôleur en
 * place.** Il porte encore l'identité du document quitté : le laisser, c'était
 * enregistrer le document posé PAR-DESSUS le document quitté à la frappe
 * suivante. Détaché, il s'enregistre comme un document neuf ; l'original et le
 * document quitté restent intacts. Le refus n'arrive en pratique que si le
 * contrôleur appartient à une autre conversation, ce que l'appelant exclut en
 * l'obtenant par cette même conversation — c'est un filet, pas un chemin.
 *
 * @returns `adopte: false` quand le document a été posé comme une copie.
 */
export function poserLIdentiteDuDocument(
  conversationId: string,
  identite: IdentiteDuDocumentPose,
): { readonly adopte: boolean } {
  const controller = canvasDocumentRuntime.getDraftController(conversationId);
  if (identite.kind === 'nouveau-document') {
    controller?.clearDraft();
    return { adopte: true };
  }
  if (!controller) return { adopte: false };
  const decision = controller.restoreDraft(identite.draftId, conversationId, identite.integrityAttemptId);
  if (decision.accepted) return { adopte: true };
  controller.clearDraft();
  return { adopte: false };
}

/** Les dépendances réelles — extraites pour que le geste soit jouable sans réseau ni SuperDoc. */
export function dependancesDeBasculeParDefaut(): DependancesDeBascule {
  return {
    lireLeContenu: (draftId) => canvasDraftApi.getDraftContent(draftId),
    enregistrerLeBrouillonCourant,
  };
}
